import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaHome,
  FaUsers,
  FaChartBar,
  FaBuilding,
  FaBed,
  FaSignOutAlt,
  FaUserEdit,
} from "react-icons/fa";
import API from "./config/API";

function FloorsRoomsBeds() {
  const [userName, setUserName] = useState("");
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [floors, setFloors] = useState([]);
  const [hoverIndex, setHoverIndex] = useState(null);

  const navigate = useNavigate();

  // =========================================================
  // FETCH FLOORS
  // =========================================================

  useEffect(() => {
    const fetchFloors = async () => {
      try {
        const loggedUser = JSON.parse(localStorage.getItem("user"));

        if (!loggedUser?.emailId) {
          navigate("/");
          return;
        }

        setUserName(loggedUser.userName || loggedUser.emailId);

        const response = await fetch(
          `${API.V1}/floor_rooms/getFloors?emailId=${encodeURIComponent(
            loggedUser.emailId,
          )}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${loggedUser.jwtToken}`,
            },
          },
        );

        if (response.status === 401) {
          alert("Session expired. Please login again.");

          localStorage.clear();
          sessionStorage.clear();

          navigate("/");
          return;
        }

        const data = await response.json();

        if (response.ok && data?.data?.existedfloors) {
          setFloors(data.data.existedfloors);
        } else {
          setFloors([]);
        }
      } catch (error) {
        console.error("Error fetching floors:", error);
        setFloors([]);
      }
    };

    fetchFloors();
  }, [navigate]);

  // =========================================================
  // LOGOUT
  // =========================================================

  const handleLogout = () => {
    localStorage.removeItem("user");
    sessionStorage.clear();
    navigate("/");
  };

  // =========================================================
  // FLOOR CLICK
  // =========================================================

  const handleFloorClick = (floorName) => {
    navigate("/floorRoomsList", {
      state: {
        floorName,
      },
    });
  };

  return (
    <div style={styles.container}>
      {/* =====================================================
          SIDEBAR
      ===================================================== */}

      <aside style={styles.sidebar}>
        <div style={styles.logoContainer}>
          <div style={styles.logoIcon}>🏢</div>

          <h2 style={styles.logo}>
            <span>Create Floor Dashboard</span>
          </h2>
        </div>

        <div style={styles.menuTitle}>MAIN MENU</div>

        <ul style={styles.menu}>
          {menuItems.map((item, index) => (
            <li key={index}>
              <Link
                to={item.path}
                style={{
                  ...styles.menuItem,
                  ...(hoverIndex === index ? styles.menuItemHover : {}),
                }}
                onMouseEnter={() => setHoverIndex(index)}
                onMouseLeave={() => setHoverIndex(null)}
              >
                <span style={styles.menuIcon}>{item.icon}</span>

                <span>{item.label}</span>
              </Link>
            </li>
          ))}
        </ul>
      </aside>

      {/* =====================================================
          MAIN AREA
      ===================================================== */}

      <main style={styles.main}>
        {/* ===================================================
            HEADER
        =================================================== */}

        <header style={styles.header}>
          {/* LEFT SIDE */}
          <div>
            <div style={styles.welcome}>👋 Hi {userName || "User"}</div>

            <div style={styles.welcomeSub}>Welcome back to your dashboard</div>
          </div>

          {/* RIGHT SIDE */}
          <div style={styles.headerRight}>
            {/* GO TO DASHBOARD BUTTON */}
            <button
              style={styles.backButton}
              onClick={() => navigate("/dashboard")}
            >
              ← Go to DashBoard
            </button>

            {/* PROFILE */}
            <div style={styles.profileContainer}>
              <img
                src="https://cdn-icons-png.flaticon.com/512/149/149071.png"
                alt="profile"
                style={styles.profileImg}
                onClick={() => setShowProfileMenu(!showProfileMenu)}
              />

              {showProfileMenu && (
                <div style={styles.profileMenu}>
                  <Link
                    to="/updateProfile"
                    style={styles.profileMenuItem}
                    onClick={() => setShowProfileMenu(false)}
                  >
                    <FaUserEdit />
                    <span>Update Profile</span>
                  </Link>

                  <div style={styles.logout} onClick={handleLogout}>
                    <FaSignOutAlt />
                    <span>Logout</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* ===================================================
            CONTENT
        =================================================== */}

        <section style={styles.content}>
          {/* PAGE TITLE */}

          <div style={styles.pageHeader}>
            <div>
              <h1 style={styles.title}>Floors Overview</h1>

              <p style={styles.subtitle}>
                Manage your floors, rooms and bed configuration
              </p>
            </div>

            <div style={styles.floorCount}>
              <FaBuilding />

              <div>
                <span style={styles.countNumber}>{floors.length}</span>

                <span style={styles.countLabel}>Floors</span>
              </div>
            </div>
          </div>

          {/* =================================================
              TABLE WRAPPER
          ================================================= */}

          <div style={styles.tableWrapper}>
            {/* TABLE HEADER */}

            <div style={styles.headerRow}>
              <div style={styles.headerCell}>Floor Name</div>

              <div style={styles.headerCell}>PG Name</div>

              <div style={styles.headerCell}>Created By</div>

              <div style={styles.headerCell}>Created Date</div>

              <div
                style={{
                  ...styles.headerCell,
                  textAlign: "center",
                }}
              >
                Rooms
              </div>

              <div
                style={{
                  ...styles.headerCell,
                  textAlign: "center",
                }}
              >
                Status
              </div>
            </div>

            {/* TABLE DATA */}

            {floors.length > 0 ? (
              floors.map((floor, index) => (
                <div
                  key={floor.floorId}
                  style={{
                    ...styles.row,
                    ...(hoverIndex === index ? styles.rowHover : {}),
                  }}
                  onMouseEnter={() => setHoverIndex(index)}
                  onMouseLeave={() => setHoverIndex(null)}
                >
                  {/* FLOOR NAME */}

                  <div style={styles.cell}>
                    <div
                      style={styles.floorName}
                      onClick={() => handleFloorClick(floor.floorName)}
                    >
                      <span style={styles.floorIcon}>🏢</span>

                      {floor.floorName}
                    </div>
                  </div>

                  {/* PG NAME */}

                  <div style={styles.cell}>{floor.pgName || "-"}</div>

                  {/* CREATED BY */}

                  <div style={styles.cell}>
                    <span style={styles.createdBy}>
                      {floor.floorCreatedBy || "-"}
                    </span>
                  </div>

                  {/* CREATED DATE */}

                  <div style={styles.cell}>{floor.createdDate || "-"}</div>

                  {/* ROOMS */}

                  <div
                    style={{
                      ...styles.cell,
                      textAlign: "center",
                    }}
                  >
                    <span style={styles.roomBadge}>
                      {floor.totalRoomsCountByFloor ?? 0}
                    </span>
                  </div>

                  {/* STATUS */}

                  <div
                    style={{
                      ...styles.cell,
                      textAlign: "center",
                    }}
                  >
                    {floor.floorActiveStatus ? (
                      <span style={styles.activeBadge}>
                        <span style={styles.statusDot}></span>
                        Active
                      </span>
                    ) : (
                      <span style={styles.inactiveBadge}>
                        <span style={styles.statusDot}></span>
                        Inactive
                      </span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div style={styles.emptyState}>
                <div style={styles.emptyIcon}>🏢</div>

                <div style={styles.emptyTitle}>No Floors Found</div>

                <div style={styles.emptyText}>
                  No floor information is available.
                </div>
              </div>
            )}
          </div>
        </section>

        {/* ===================================================
            FOOTER
        =================================================== */}

        <footer style={styles.footer}>
          <span>SVK Solutions</span>

          <span>© 2026 All Rights Reserved</span>
        </footer>
      </main>
    </div>
  );
}

export default FloorsRoomsBeds;

// =========================================================
// MENU ITEMS
// =========================================================

const menuItems = [
  {
    label: "Dashboard",
    icon: <FaChartBar />,
    path: "/floorRoomsBeds",
  },

  {
    label: "Setup Config",
    icon: <FaHome />,
    path: "/configurationSetUpCount",
  },

  {
    label: "Create Floors",
    icon: <FaUsers />,
    path: "/createfloors",
  },

  {
    label: "Create Rooms",
    icon: <FaBuilding />,
    path: "/createAddRoomForm",
  },

  {
    label: "Create Beds",
    icon: <FaBed />,
    path: "/floorRoomBedSelector",
  },
];

// =========================================================
// STYLES
// =========================================================

const styles = {
  // =======================================================
  // MAIN CONTAINER
  // =======================================================

  container: {
    display: "flex",
    minHeight: "100vh",
    width: "100%",
    fontFamily: "'Poppins', 'Segoe UI', sans-serif",
    background:
      "linear-gradient(135deg, #6f8fa8 0%, #b83b8f 45%, #765238 100%)",
    overflow: "hidden",
  },

  // =======================================================
  // SIDEBAR
  // =======================================================

  sidebar: {
    width: "240px",
    minWidth: "240px",
    height: "100vh",
    background: "rgba(25, 20, 35, 0.48)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    borderRight: "1px solid rgba(255,255,255,0.12)",
    padding: "25px 16px",
    boxSizing: "border-box",
    boxShadow: "8px 0 30px rgba(0,0,0,0.20)",
    position: "sticky",
    top: 0,
  },

  logoContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
    marginBottom: "35px",
  },

  logoIcon: {
    width: "45px",
    height: "45px",
    borderRadius: "12px",
    background: "rgba(255,255,255,0.15)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "24px",
  },

  logo: {
    margin: 0,
    color: "#fff",
    fontSize: "23px",
    fontWeight: "800",
    letterSpacing: "1px",
    display: "flex",
    flexDirection: "column",
    lineHeight: "1.1",
  },

  menuTitle: {
    color: "rgba(255,255,255,0.5)",
    fontSize: "11px",
    fontWeight: "700",
    letterSpacing: "1.5px",
    padding: "0 12px",
    marginBottom: "12px",
  },

  menu: {
    listStyle: "none",
    padding: 0,
    margin: 0,
  },

  menuItem: {
    display: "flex",
    alignItems: "center",
    gap: "13px",
    padding: "13px 14px",
    marginBottom: "7px",
    borderRadius: "12px",
    color: "#e2e8f0",
    textDecoration: "none",
    fontSize: "14px",
    fontWeight: "500",
    transition: "all .25s ease",
  },

  menuIcon: {
    width: "20px",
    display: "flex",
    justifyContent: "center",
    fontSize: "16px",
  },

  menuItemHover: {
    background: "linear-gradient(135deg, #8b5e3c, #b07d52)",
    color: "#fff",
    transform: "translateX(5px)",
    boxShadow: "0 8px 18px rgba(0,0,0,.20)",
  },

  // =======================================================
  // MAIN
  // =======================================================

  main: {
    flex: 1,
    minWidth: 0,
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    padding: "18px",
    boxSizing: "border-box",
  },

  // =======================================================
  // HEADER
  // =======================================================

  header: {
    minHeight: "70px",
    background: "rgba(255,255,255,0.13)",
    backdropFilter: "blur(18px)",
    WebkitBackdropFilter: "blur(18px)",
    border: "1px solid rgba(255,255,255,0.10)",
    borderRadius: "16px",
    padding: "0 22px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    boxShadow: "0 8px 25px rgba(0,0,0,.12)",
    marginBottom: "18px",
    position: "relative",
    flexShrink: 0,
  },

  welcome: {
    color: "#fff",
    fontWeight: "700",
    fontSize: "17px",
  },

  welcomeSub: {
    color: "rgba(255,255,255,.68)",
    fontSize: "12px",
    marginTop: "3px",
  },

  // =======================================================
  // HEADER RIGHT SIDE
  // =======================================================

  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },

  // =======================================================
  // GO TO DASHBOARD BUTTON
  // =======================================================

  backButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    padding: "8px 13px",
    border: "1px solid rgba(255,255,255,.28)",
    borderRadius: "8px",
    background: "rgba(255,255,255,.12)",
    color: "#fff",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all .25s ease",
    whiteSpace: "nowrap",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
  },

  // =======================================================
  // PROFILE
  // =======================================================

  profileContainer: {
    position: "relative",
  },

  profileImg: {
    width: "42px",
    height: "42px",
    borderRadius: "50%",
    cursor: "pointer",
    border: "2px solid rgba(255,255,255,.7)",
    boxShadow: "0 4px 12px rgba(0,0,0,.25)",
  },

  profileMenu: {
    position: "absolute",
    top: "52px",
    right: 0,
    width: "190px",
    background: "#fff",
    borderRadius: "12px",
    overflow: "hidden",
    boxShadow: "0 15px 35px rgba(0,0,0,.25)",
    zIndex: 999,
  },

  profileMenuItem: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "13px 15px",
    textDecoration: "none",
    color: "#334155",
    fontWeight: "600",
    fontSize: "14px",
  },

  logout: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "13px 15px",
    color: "#ef4444",
    cursor: "pointer",
    borderTop: "1px solid #e2e8f0",
    fontWeight: "600",
    fontSize: "14px",
  },

  // =======================================================
  // CONTENT
  // =======================================================

  content: {
    flex: 1,
    minHeight: 0,
    background: "rgba(255,255,255,0.10)",
    backdropFilter: "blur(15px)",
    WebkitBackdropFilter: "blur(15px)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "18px",
    padding: "22px",
    boxSizing: "border-box",
    overflowY: "auto",
    overflowX: "hidden",
    boxShadow: "0 10px 30px rgba(0,0,0,.15)",
    marginBottom: "12px",
  },

  // =======================================================
  // PAGE HEADER
  // =======================================================

  pageHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "15px",
    marginBottom: "20px",
  },

  title: {
    margin: 0,
    color: "#fff",
    fontSize: "26px",
    fontWeight: "800",
  },

  subtitle: {
    margin: "5px 0 0",
    color: "rgba(255,255,255,.70)",
    fontSize: "13px",
  },

  floorCount: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    padding: "10px 15px",
    background: "rgba(255,255,255,.12)",
    border: "1px solid rgba(255,255,255,.12)",
    borderRadius: "12px",
    color: "#fff",
  },

  countNumber: {
    display: "block",
    fontSize: "20px",
    fontWeight: "800",
    lineHeight: 1,
  },

  countLabel: {
    display: "block",
    marginTop: "3px",
    fontSize: "11px",
    color: "rgba(255,255,255,.65)",
  },

  // =======================================================
  // TABLE
  // =======================================================

  tableWrapper: {
    width: "100%",
    overflowX: "auto",
    paddingBottom: "5px",
  },

  headerRow: {
    minWidth: "850px",
    display: "grid",
    gridTemplateColumns: "1.35fr 1.35fr 1.45fr 1.25fr .7fr .95fr",
    background: "linear-gradient(90deg,#6f4e37,#a47149)",
    color: "#fff",
    padding: "14px 16px",
    borderRadius: "11px",
    fontWeight: "700",
    fontSize: "13px",
    alignItems: "center",
    boxSizing: "border-box",
    marginBottom: "8px",
  },

  headerCell: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    paddingRight: "10px",
  },

  row: {
    minWidth: "850px",
    display: "grid",
    gridTemplateColumns: "1.35fr 1.35fr 1.45fr 1.25fr .7fr .95fr",
    padding: "14px 16px",
    marginBottom: "8px",
    background: "rgba(255,255,255,.09)",
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    border: "1px solid rgba(255,255,255,.06)",
    borderRadius: "11px",
    color: "#fff",
    transition: "all .25s ease",
    alignItems: "center",
    boxSizing: "border-box",
    fontSize: "13px",
  },

  rowHover: {
    transform: "translateY(-2px)",
    background: "rgba(255,255,255,.17)",
    boxShadow: "0 8px 20px rgba(0,0,0,.15)",
  },

  cell: {
    minWidth: 0,
    paddingRight: "10px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },

  // =======================================================
  // FLOOR
  // =======================================================

  floorName: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: "#f8d7a6",
    cursor: "pointer",
    fontWeight: "700",
    fontSize: "14px",
    transition: ".2s",
  },

  floorIcon: {
    fontSize: "16px",
  },

  createdBy: {
    color: "rgba(255,255,255,.82)",
  },

  // =======================================================
  // ROOM BADGE
  // =======================================================

  roomBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: "30px",
    height: "26px",
    padding: "0 8px",
    borderRadius: "7px",
    background: "rgba(59,130,246,.25)",
    border: "1px solid rgba(147,197,253,.35)",
    color: "#dbeafe",
    fontWeight: "700",
  },

  // =======================================================
  // STATUS
  // =======================================================

  activeBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    padding: "5px 9px",
    borderRadius: "20px",
    background: "rgba(34,197,94,.18)",
    border: "1px solid rgba(74,222,128,.25)",
    color: "#bbf7d0",
    fontWeight: "700",
    fontSize: "11px",
  },

  inactiveBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    padding: "5px 9px",
    borderRadius: "20px",
    background: "rgba(239,68,68,.18)",
    border: "1px solid rgba(248,113,113,.25)",
    color: "#fecaca",
    fontWeight: "700",
    fontSize: "11px",
  },

  statusDot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
    background: "currentColor",
  },

  // =======================================================
  // EMPTY
  // =======================================================

  emptyState: {
    padding: "60px 20px",
    textAlign: "center",
    background: "rgba(255,255,255,.07)",
    borderRadius: "12px",
    color: "#fff",
  },

  emptyIcon: {
    fontSize: "40px",
    marginBottom: "10px",
  },

  emptyTitle: {
    fontSize: "18px",
    fontWeight: "700",
  },

  emptyText: {
    marginTop: "5px",
    color: "rgba(255,255,255,.60)",
    fontSize: "13px",
  },

  // =======================================================
  // FOOTER
  // =======================================================

  footer: {
    minHeight: "38px",
    flexShrink: 0,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "0 10px",
    color: "rgba(255,255,255,.72)",
    fontSize: "11px",
  },
};
